import {
  createAuthFetch,
  isSsoRedirectError,
  type AuthFetchEnv,
} from "@axisnode/auth-client";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * Generic session shape — Platform chrome only.
 * No Native-System fields (no project/document/room domains).
 */
export type PlatformSession = {
  status: "loading" | "authenticated" | "anonymous";
  /** Verified email when the session endpoint returns one. */
  email: string | null;
  /** Opaque actor/subject id when present — never interpreted by Shell. */
  subject: string | null;
  /** Raw JSON from the session endpoint for consumers that need more. */
  raw: Record<string, unknown> | null;
};

export type SessionProviderProps = {
  children: ReactNode;
  /** API origin, e.g. https://api.axisnode.site */
  apiBaseUrl: string;
  /** Session probe path — default `/auth/me` (platform SSO surface). */
  sessionPath?: string;
  auth?: AuthFetchEnv;
  /** Skip the network probe (unit tests). */
  initialSession?: PlatformSession;
};

type SessionContextValue = {
  session: PlatformSession;
  refresh: () => Promise<void>;
};

const SessionContext = createContext<SessionContextValue | null>(null);

const ANON: PlatformSession = {
  status: "anonymous",
  email: null,
  subject: null,
  raw: null,
};

function parseSession(raw: Record<string, unknown>): PlatformSession {
  const email =
    typeof raw.email === "string"
      ? raw.email
      : typeof raw.preferred_username === "string"
        ? raw.preferred_username
        : null;
  const subject =
    typeof raw.sub === "string"
      ? raw.sub
      : typeof raw.actor_id === "string"
        ? raw.actor_id
        : typeof raw.user_id === "string"
          ? raw.user_id
          : null;
  return { status: "authenticated", email, subject, raw };
}

export function SessionProvider({
  children,
  apiBaseUrl,
  sessionPath = "/auth/me",
  auth,
  initialSession,
}: SessionProviderProps) {
  const [session, setSession] = useState<PlatformSession>(
    initialSession ?? { status: "loading", email: null, subject: null, raw: null },
  );

  const authFetch = useMemo(
    () =>
      createAuthFetch({
        ...(auth ?? {}),
        // Probe only — AuthGate owns the SSO navigation UX.
        redirectOn401: false,
      }),
    // auth injectables are expected to be stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const refresh = useCallback(async () => {
    if (initialSession) {
      setSession(initialSession);
      return;
    }
    const base = apiBaseUrl.replace(/\/+$/, "");
    const path = sessionPath.startsWith("/") ? sessionPath : `/${sessionPath}`;
    try {
      const res = await authFetch(`${base}${path}`);
      if (res.status === 401) {
        setSession(ANON);
        return;
      }
      if (!res.ok) {
        setSession(ANON);
        return;
      }
      const raw = (await res.json()) as Record<string, unknown>;
      setSession(parseSession(raw));
    } catch (err) {
      if (isSsoRedirectError(err)) {
        // Redirect in flight — keep loading so UI doesn't flash onboarding.
        setSession({
          status: "loading",
          email: null,
          subject: null,
          raw: null,
        });
        return;
      }
      setSession(ANON);
    }
  }, [apiBaseUrl, sessionPath, authFetch, initialSession]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo(() => ({ session, refresh }), [session, refresh]);

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSession must be used within <SessionProvider>");
  }
  return ctx;
}
